// noinspection JSUnusedGlobalSymbols

import { type ToolbarItem } from "devextreme/ui/popup";
import { type ComponentInternalInstance, ref, watch } from "vue";
import { DxButton } from "devextreme-vue/button";
import { cancelButtonProps, noButtonProps, okButtonProps, yesButtonProps } from "@helpers/button-helper";

interface InternalToolbarItem extends ToolbarItem {
    ___id: string;
}

export interface ToolbarReference<T> {
    get id(): string;

    update(updateFn: (widget: T) => void): void;
}

type ToolbarItemOptionsWithoutWidget = Omit<ToolbarItem, "widget" | "options">;

export const modalDefinedEmits = ["onCloseRequest", "onToolbarChanged"];


// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useModalBuilder() {

    let context: ComponentInternalInstance | null = null;
    const toolbarItems = ref<InternalToolbarItem[]>([]);
    const toolbarItemsMap = new Map<string, InternalToolbarItem>();
    const dxItems = ref<ToolbarItem[]>([]);

    watch(toolbarItems, (newVal) => {
        dxItems.value = newVal.map(x => ({ ...x }));
        context?.emit("onToolbarChanged", dxItems.value);
    }, { deep: true });


    function setContext(componentContext: ComponentInternalInstance): void {
        context = componentContext
    }


    function add(item: InternalToolbarItem): ToolbarReference<DxButton> {
        toolbarItems.value.push(item);
        const reactiveButton = (toolbarItems.value as InternalToolbarItem[]).at(-1);
        toolbarItemsMap.set(item.___id, reactiveButton as InternalToolbarItem);



        return {
            get id() {
                return item.___id;
            },
            update(updateFn: (widget: DxButton) => void) {
                updateFn(reactiveButton!.options as DxButton)
            }
        } as ToolbarReference<DxButton>;


    }

    function addButton(id: string, widgetOptions: DxButton, itemOptions?: ToolbarItemOptionsWithoutWidget):
        ToolbarReference<DxButton> {
        const button: InternalToolbarItem = {
            ___id: id,
            widget: "dxButton",
            options: { ...widgetOptions }, ...itemOptions
        };

        return add(button);
    }

    function remove(widget: string | ToolbarReference<unknown>): void {
        const id = typeof widget === "string" ? widget : widget.id;
        const idx = toolbarItems.value.findIndex(x => x.___id === id);
        if (idx > -1) {
            toolbarItems.value.splice(idx, 1);
            toolbarItemsMap.delete(id);
        }
    }

    function close(returnValue?: unknown): void {
        context?.emit("onCloseRequest", returnValue);
    }

    const standardButtons = {
        addOk: function (): ToolbarReference<DxButton> {
            return addButton("ok", okButtonProps(), { toolbar: "bottom", location: "after" })
        },
        addOkCancel: function (): { okButton: ToolbarReference<DxButton>, cancelButton: ToolbarReference<DxButton> } {
            return {
                cancelButton: addButton("cancel", cancelButtonProps(), { toolbar: "bottom", location: "after" }),
                okButton: addButton("ok", okButtonProps(), { toolbar: "bottom", location: "after" })
            }
        },
        addYesNo: function (): { yesButton: ToolbarReference<DxButton>, noButton: ToolbarReference<DxButton> } {
            return {
                noButton: addButton("no", noButtonProps(), { toolbar: "bottom", location: "after" }),
                yesButton: addButton("yes", yesButtonProps(), { toolbar: "bottom", location: "after" })

            }
        },
        addYesNoCancel: function (): {
            yesButton: ToolbarReference<DxButton>, noButton: ToolbarReference<DxButton>
            , cancelButton: ToolbarReference<DxButton>
        } {
            return {
                yesButton: addButton("yes", yesButtonProps(), { toolbar: "bottom", location: "after" }),
                noButton: addButton("no", noButtonProps(), { toolbar: "bottom", location: "after" }),
                cancelButton: addButton("no", cancelButtonProps(), { toolbar: "bottom", location: "after" }),
            }
        }

    }

    return {
        addButton,
        setContext,
        remove,
        close,
        standardButtons: standardButtons,
    };
}